// Phase 5 (web revival) — root app shell.
//
// Top nav, footer-less, content router-rendered. All authed routes mount
// this; /login renders standalone (no nav chrome on the auth screen).

import { Link, Outlet, useNavigate } from '@tanstack/react-router';
import { useSyncExternalStore, useState } from 'react';
import { snapshot, subscribe, clearSession } from '../app/auth';
import { api } from '../api/client';
import { disconnect as disconnectWS } from '../app/wsClient';

function useAuth() {
  return useSyncExternalStore(subscribe, snapshot);
}

export function AppLayout() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);

  async function handleSignOut() {
    setSigningOut(true);
    try {
      // Best-effort: revoke the refresh token server-side. If it fails
      // (offline, server down, refresh cookie already gone), we still
      // clear local state so the UI never gets stuck.
      await api.post('/v1/auth/logout', {}).catch(() => undefined);
    } finally {
      disconnectWS();
      clearSession();
      setSigningOut(false);
      navigate({ to: '/login' });
    }
  }

  const navItem =
    'rounded-full px-3 py-1 text-[13px] font-medium transition-colors';
  const activeProps = { className: `${navItem} bg-white/10 text-white` };
  const inactiveProps = {
    className: `${navItem} text-neutral-400 hover:text-neutral-100`,
  };

  const displayName = auth.user?.display_name ?? auth.user?.apple_sub ?? 'me';
  const initial = displayName.trim().charAt(0).toUpperCase() || '·';

  return (
    <div className="flex h-full flex-col bg-neutral-950">
      <header className="flex min-h-12 shrink-0 items-center gap-2 border-b border-white/5 px-4 pt-[env(safe-area-inset-top)]">
        <Link
          to="/library"
          className="mr-4 font-display text-[19px] font-medium italic tracking-tight text-neutral-50"
        >
          DVPhoto
        </Link>
        <nav className="flex flex-1 items-center gap-1">
          <Link to="/library" activeProps={activeProps} inactiveProps={inactiveProps}>
            Library
          </Link>
          <Link
            to="/shared-with-me"
            activeProps={activeProps}
            inactiveProps={inactiveProps}
          >
            Shared
          </Link>
          <Link to="/albums" activeProps={activeProps} inactiveProps={inactiveProps}>
            Albums
          </Link>
        </nav>
        <div className="flex items-center gap-1.5">
          <Link
            to="/settings"
            title={`${displayName} — settings`}
            className="flex size-7 items-center justify-center rounded-full border border-white/10 bg-neutral-900 text-[11px] font-semibold uppercase text-neutral-300 transition-colors hover:border-white/25 hover:text-white"
          >
            {initial}
          </Link>
          <button
            type="button"
            onClick={handleSignOut}
            disabled={signingOut}
            aria-label="Sign out"
            title="Sign out"
            className="flex size-7 items-center justify-center rounded-full text-neutral-500 transition-colors hover:bg-white/10 hover:text-neutral-200 disabled:opacity-50"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
        </div>
      </header>
      <main className="flex-1 overflow-hidden">
        <Outlet />
      </main>
    </div>
  );
}
